import type { FragmentType } from 'src/gql';
import { omit, sum } from 'es-toolkit';
import { Area, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { GraphTooltip } from 'src/components/portfolio/views/graph-tooltip';
import { GRAPH_HEIGHT } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { formatDateToMMDDYY, formatLargeUSDPrecision } from 'src/utils/formatting';

import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { graphColors, greyIconsColor, neutral, primaryColors } from '@savvy/deprecated-shared/theme';
import { generateDataForPortfolioItemGroups } from '@savvy/deprecated-shared/utils/graphs';

export const NetWorthGraphFragment = graphql(/* GraphQL */ `
  fragment NetWorthGraph on PortfolioItemGroup {
    name
    accounts {
      __typename
    }
    assets {
      __typename
    }
    hiddenFromClientHistoricalValues {
      date
      value {
        amount
        currencyCode
      }
    }
    historicalValues {
      date
      value {
        amount
        currencyCode
      }
    }
    liabilities {
      __typename
    }
  }
`);

type NetWorthGraphProps = {
  readonly portfolioItemGroupFrags: FragmentType<typeof NetWorthGraphFragment>[];
  readonly lookbackPeriodDays: number;
  readonly showHiddenClientAssetsInGraph: boolean;
};

function formatDataForNetWorth(
  data: {
    name: string;
  }[],
) {
  return data.map((dateData) => ({
    name: dateData.name,
    amount: sum(Object.values(omit(dateData, ['name'])).map((valueString) => Number(valueString))),
  }));
}

export function NetWorthGraph({
  portfolioItemGroupFrags,
  lookbackPeriodDays,
  showHiddenClientAssetsInGraph,
}: NetWorthGraphProps) {
  const data = generateDataForPortfolioItemGroups(
    portfolioItemGroupFrags.map((frag) => unmaskFragment(NetWorthGraphFragment, frag)),
    lookbackPeriodDays,
    showHiddenClientAssetsInGraph,
  );

  const isSmallScreen = useIsSmallScreen();

  return (
    <div className="overflow-hidden">
      <ResponsiveContainer width="100%" height={GRAPH_HEIGHT}>
        <ComposedChart data={formatDataForNetWorth(data)} margin={{ left: 0, right: 0 }}>
          <defs>
            <linearGradient id="amountGradient" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={isSmallScreen ? neutral[0] : graphColors.linearGradient}
                stopOpacity={isSmallScreen ? 0.3 : 1}
              />
              <stop offset="100%" stopColor={isSmallScreen ? neutral[0] : graphColors.linearGradient} stopOpacity={0} />
            </linearGradient>
          </defs>
          {isSmallScreen ? (
            <>
              <XAxis
                dataKey="name"
                stroke="#F0F0F0"
                strokeWidth="0"
                tick={false}
                style={{ fontSize: '0.7rem', fill: greyIconsColor }}
              />
              <YAxis hide={true} domain={['auto', 'auto']} />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                stroke={graphColors.grayGrid}
                strokeWidth="0.3px"
                tickLine={false}
                tickFormatter={(tick: string) => formatDateToMMDDYY(tick)}
                style={{ fontSize: '0.7rem', fill: greyIconsColor }}
                interval="preserveStart"
                minTickGap={100}
              />
              <YAxis
                stroke={graphColors.grayGrid}
                strokeWidth="0.3px"
                tickLine={false}
                tickFormatter={(tick) => formatLargeUSDPrecision(tick, 3)}
                style={{ fontSize: '0.7rem', fill: greyIconsColor }}
                domain={['auto', 'auto']}
                orientation="right"
                width={50}
              />
              <CartesianGrid vertical={false} stroke={graphColors.grayGrid} />
            </>
          )}
          <Tooltip content={<GraphTooltip />} />
          <Area
            dataKey="amount"
            stroke={isSmallScreen ? primaryColors.gold : graphColors.totalValue}
            fillOpacity={1}
            fill="url(#amountGradient)"
            connectNulls
            animationDuration={500}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
